"use client"

import { useGSAP } from "@gsap/react"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

gsap.registerPlugin(useGSAP, ScrollTrigger)

/**
 * Home-page animation choreography (GSAP per CLAUDE.md). Renders nothing —
 * mounted once by the home page, it animates the SERVER-rendered sections via
 * `data-*` hooks, so every section stays an RSC and the page works untouched
 * without JS (from-tweens only, no pre-hidden content).
 *
 * Sequence: hero entrance timeline on load (badge → headline → tagline → CTAs
 * → photo card), then scroll-triggered reveals for the stats strip, staff
 * announcements, category tiles (playful back-out pop), and featured cards.
 * The hero photo card also gets a slow scrubbed parallax drift.
 *
 * All of it sits inside `gsap.matchMedia("(prefers-reduced-motion:
 * no-preference)")` — reduced-motion users get the static page. Text is
 * animated per element, never split into chars, so RTL/Persian shaping is
 * never broken.
 */
export function HomeAnimations() {
  useGSAP((_context, contextSafe) => {
    const has = (selector: string) => document.querySelector(selector) !== null

    const setup = contextSafe!(() => {
      const mm = gsap.matchMedia()

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        const rtl = document.documentElement.dir === "rtl"

        // --- hero entrance -----------------------------------------------------
        // fromTo (NOT from): `from` records the element's CURRENT opacity as the
        // tween's END. On a soft (next/link) nav a re-run of this setup can fire
        // while a prior interrupted tween left the element mid-fade, so `from`
        // captures e.g. 0.67 as the target and strands it there forever. fromTo
        // pins an explicit visible end-state, so every re-run is idempotent.
        const tl = gsap.timeline({ defaults: { ease: "power3.out" } })
        if (has("[data-hero-badge]")) {
          tl.fromTo(
            "[data-hero-badge]",
            { autoAlpha: 0, y: 12 },
            { autoAlpha: 1, y: 0, duration: 0.45 }
          )
        }
        if (has("[data-hero-title]")) {
          tl.fromTo(
            "[data-hero-title]",
            { autoAlpha: 0, y: 28 },
            { autoAlpha: 1, y: 0, duration: 0.6 },
            "-=0.25"
          )
        }
        if (has("[data-hero-sub]")) {
          tl.fromTo(
            "[data-hero-sub]",
            { autoAlpha: 0, y: 18 },
            { autoAlpha: 1, y: 0, duration: 0.5 },
            "-=0.35"
          )
        }
        // The container div, NOT the Link children: those anchors are client
        // components that hydration can re-render mid-tween, stranding them at
        // the from-state. The plain server-rendered div is stable.
        if (has("[data-hero-cta]")) {
          tl.fromTo(
            "[data-hero-cta]",
            { autoAlpha: 0, y: 16 },
            { autoAlpha: 1, y: 0, duration: 0.45 },
            "-=0.3"
          )
        }
        if (has("[data-hero-card]")) {
          tl.fromTo(
            "[data-hero-card]",
            { autoAlpha: 0, x: rtl ? -48 : 48, rotation: rtl ? -8 : 8 },
            { autoAlpha: 1, x: 0, rotation: 0, duration: 0.7 },
            "-=0.55"
          )
          // Slow parallax drift while the hero scrolls out.
          gsap.to("[data-hero-card]", {
            y: 48,
            ease: "none",
            scrollTrigger: {
              trigger: "[data-hero]",
              start: "top top",
              end: "bottom top",
              scrub: true,
            },
          })
        }

        // --- scroll reveals ----------------------------------------------------
        if (has("[data-stat]")) {
          gsap.fromTo(
            "[data-stat]",
            { autoAlpha: 0, y: 20 },
            {
              autoAlpha: 1,
              y: 0,
              duration: 0.5,
              stagger: 0.08,
              ease: "power2.out",
              scrollTrigger: { trigger: "[data-stats]", start: "top 85%" },
            }
          )
        }

        if (has("[data-announcement]")) {
          gsap.fromTo(
            "[data-announcement]",
            { autoAlpha: 0, y: 32 },
            {
              autoAlpha: 1,
              y: 0,
              duration: 0.6,
              stagger: 0.12,
              ease: "power3.out",
              scrollTrigger: {
                trigger: "[data-announcements]",
                start: "top 85%",
              },
            }
          )
        }

        if (has("[data-category-tile]")) {
          gsap.fromTo(
            "[data-category-tile]",
            { autoAlpha: 0, y: 24, scale: 0.9 },
            {
              autoAlpha: 1,
              y: 0,
              scale: 1,
              duration: 0.45,
              stagger: 0.05,
              ease: "back.out(1.6)",
              scrollTrigger: { trigger: "[data-categories]", start: "top 80%" },
            }
          )
        }

        if (has("[data-featured-card]")) {
          gsap.fromTo(
            "[data-featured-card]",
            { autoAlpha: 0, y: 32 },
            {
              autoAlpha: 1,
              y: 0,
              duration: 0.55,
              stagger: 0.1,
              ease: "power3.out",
              scrollTrigger: { trigger: "[data-featured]", start: "top 80%" },
            }
          )
        }
      })

      // Client-side (next/link) navigation fires no window `load`, so
      // ScrollTrigger never auto-refreshes and its start/end positions stay
      // measured against the PREVIOUS page — above-the-fold triggers then never
      // fire and their `from` targets strand hidden. Recompute against the now-
      // settled layout (we're already two frames past mount) so in-view sections
      // reveal. Harmless on hard load (positions already correct).
      ScrollTrigger.refresh()
    })

    // Two frames after mount: selective hydration can still be swapping nodes
    // in the very first frame — binding tweens to those nodes leaves the final
    // DOM stuck at the from-state. contextSafe keeps the deferred setup inside
    // the useGSAP context so cleanup still reverts everything.
    const raf = requestAnimationFrame(() => requestAnimationFrame(setup))
    return () => cancelAnimationFrame(raf)
  })

  return null
}
